import { Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useForgotPassword } from "@/lib/api";
import { logger } from "@/lib/logger";

export function ForgotPasswordPage() {
  const forgotPasswordMutation = useForgotPassword();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const isLoading = forgotPasswordMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      logger.debug("[forgot-password] submitting for", email);
      // The backend always responds 2xx for a syntactically valid request
      // regardless of whether the account exists, so surfacing the generic
      // confirmation here does not leak account existence. A thrown error means
      // a transport/config failure (network down, BACKEND_URL missing).
      await forgotPasswordMutation.mutateAsync({ email });
      setSubmitted(true);
    } catch (err) {
      logger.error("[forgot-password] unexpected submit error:", err);
      setError("Password reset is temporarily unavailable. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="bg-accent-light/30 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full">
              <Mail className="text-accent h-6 w-6" />
            </div>
            <CardTitle className="text-primary text-2xl font-normal">Check your email</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted text-sm">
              If an account exists for that email, we&apos;ve sent a password reset link. Check your
              inbox.
            </p>
            <Button
              asChild
              className="bg-accent text-accent-foreground hover:bg-accent/90 mt-6 w-full"
            >
              <Link to="/login">Back to sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-primary text-2xl font-normal">Reset your password</CardTitle>
          <p className="text-muted text-sm">
            Enter your email and we&apos;ll send you a reset link
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Sending...
                </>
              ) : (
                "Send reset link"
              )}
            </Button>

            <p className="text-muted text-center text-sm">
              Remember your password?{" "}
              <Link to="/login" className="text-accent hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
